// confirm_suspension (WF-016, CORE-063): the PH declares the site is left safe, or the
// RS confirms the stop (the AA on sandboxes before 0016). When both exist the cycle is Suspended. Nothing here restarts
// work: that needs a new daily cycle.
import { useId, useState } from 'react'
import { RuleRefs } from '@/components/evidence'
import { Banner, Button, EmptyState, Field, Select, TextArea } from '@/components/ui'
import type { ActionPanelProps } from '@/features/contracts'
import { fmtDateTime } from '@/lib/format'
import { useCommand } from '@/lib/queries'
import {
  confirmationRoleForAction,
  defaultSuspensionId,
  expectedVersionFor,
  findSuspension,
  hasPhDeclaration,
  reasonLabel,
  roleLabel,
  stringArg,
  suspensionChoices,
} from '../logic'
import { CommandFailure, NotAvailable, PanelFooter, SigningStatement, Tick, Unanswered } from '../parts'
import { SuspensionRecord } from '../StopRecord'

export function ConfirmSuspensionPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const cmd = useCommand('confirm_suspension')
  const selectId = useId()
  const notesId = useId()
  // Sign in the role the server offered: RS from 0016 (AA only on older sandboxes).
  const role = confirmationRoleForAction(action)
  const suggestedId = stringArg(action, 'suspension_id')
  const [suspensionId, setSuspensionId] = useState(() => defaultSuspensionId(suggestedId, suspensionChoices(bundle, role, suggestedId)))
  const [safe, setSafe] = useState(false)
  const [notes, setNotes] = useState('')

  // The page refetches every 30 s. Keep the chosen stop in the list even if it has since been
  // confirmed elsewhere, so the select always shows exactly what would be sent.
  const chosen = findSuspension(bundle, suspensionId)
  const open = suspensionChoices(bundle, role, suggestedId)
  const choices = chosen && !open.some((c) => c.suspension.id === chosen.suspension.id) ? [chosen, ...open] : open
  const touch = () => {
    if (cmd.error) cmd.resetIntent()
  }

  if (!role) {
    return (
      <div className="space-y-4">
        <Banner tone="bad" title="This step is for the Permit Holder or the Responsible Supervisor">
          The server did not say which of the two roles you would sign in, so nothing can be signed here.
        </Banner>
        <PanelFooter>
          <Button variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </PanelFooter>
      </div>
    )
  }

  const isPh = role === 'PH'
  const supervisor = roleLabel(role)
  const missing = chosen ? [] : ['Choose the stop you are confirming']

  const submit = async () => {
    if (!chosen) return
    try {
      await cmd.run(
        {
          permit_id: bundle.permit.id,
          suspension_id: chosen.suspension.id,
          confirmation_role: role,
          safe_state_confirmed: safe,
          notes: notes.trim() || null,
        },
        expectedVersionFor('confirm_suspension', bundle),
      )
      onDone()
    } catch {
      /* shown by CommandFailure */
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink">
        {isPh
          ? 'Declare, as Permit Holder, that the work has stopped and you left the site and equipment safe.'
          : `Confirm, as ${supervisor}, that the stop is in force and the site is in a safe state.`}
        <RuleRefs ruleIds={['CORE-063']} />
      </p>

      <NotAvailable action={action} />

      {choices.length === 0 ? (
        <EmptyState title="No stop is waiting for your confirmation">
          {isPh ? 'Every stop already has a PH declaration.' : 'Every stop already has a supervisor confirmation.'}
        </EmptyState>
      ) : (
        <Field label="Which stop are you confirming?" required htmlFor={selectId}>
          <Select
            id={selectId}
            value={suspensionId}
            onChange={(e) => {
              touch()
              setSuspensionId(e.target.value)
            }}
            placeholder="Choose the stop"
            options={choices.map((c) => ({
              value: c.suspension.id,
              label: `Cycle #${c.cycle.cycle_number} - ${reasonLabel(c.suspension.reason_code)} - received ${fmtDateTime(c.suspension.server_received_at)}`,
            }))}
          />
        </Field>
      )}

      {chosen ? <SuspensionRecord s={chosen.suspension} cycle={chosen.cycle} showCycle /> : null}

      {chosen && !isPh && !hasPhDeclaration(chosen.suspension) ? (
        <Banner tone="warn" title="No PH declaration yet">
          The Permit Holder has not signed this stop or declared the site safe. The server refuses your confirmation until the PH
          has (PH_DECLARATION_MISSING) - nobody can sign for the PH.
        </Banner>
      ) : null}

      {chosen ? (
        <>
          <div className="rounded-lg border border-line bg-white px-3">
            <Tick
              checked={safe}
              onChange={(v) => {
                touch()
                setSafe(v)
              }}
              label={
                isPh
                  ? 'I have stopped the work and left the site and equipment safe.'
                  : 'I have checked: the work has stopped and the site is in a safe state.'
              }
            />
          </div>

          <Field
            label="Notes (optional)"
            htmlFor={notesId}
            hint={isPh ? 'For example, how you left the site, or what the next shift needs to know.' : 'For example, the status agreed with the PH, or follow-up needed.'}
          >
            <TextArea
              id={notesId}
              value={notes}
              onChange={(e) => {
                touch()
                setNotes(e.target.value)
              }}
            />
          </Field>

          <SigningStatement>
            {isPh ? (
              <p>
                You sign as <strong>Permit Holder</strong> that the work on daily cycle #{chosen.cycle.cycle_number} has stopped and
                that you left the site and equipment safe.
              </p>
            ) : (
              <p>
                You sign as <strong>{supervisor}</strong> that this stop on daily cycle #{chosen.cycle.cycle_number} is in force
                and the site is in a safe state. When the PH declaration and your confirmation are both recorded, the cycle is
                Suspended.
              </p>
            )}
            <p>This does not restart work. Work can restart only with a new daily cycle that is validated again.</p>
          </SigningStatement>
        </>
      ) : null}

      <CommandFailure error={cmd.error} />
      <Unanswered items={choices.length > 0 ? missing : []} />

      <PanelFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} loading={cmd.isPending} disabled={!chosen || !action.enabled}>
          {isPh ? 'Sign as Permit Holder' : `Confirm as ${role === 'AA' ? 'Area Authority' : 'Responsible Supervisor'}`}
        </Button>
      </PanelFooter>
    </div>
  )
}
